/**
 * Session assessment draft state — pure helpers for multi-row edit + batch save.
 * Used by EditAssessmentPilot / EditAssessmentInstructor (web/mobile parity).
 *
 * @see docs/architecture/web-session-assessment-batch-save.md
 */

import {
  buildOverrideRecord,
  sortByFrameworkListingOrder,
  type OverrideCategory,
} from '@orca360/shared';
import type { BatchAssessmentUpdate } from './assessment-versions';

/** Minimum row shape loaded from assessments_pilot / assessments_instructor. */
export interface SessionAssessmentRow {
  id: string;
  score: number | null;
  analysis: string | null;
  goals: string | null;
  evidence: unknown;
  // HG-3A G3-02b, pilot only. Absent on instructor rows, which are graded per
  // IEC criterion and carry no dimensions group.
  how_many?: string | null;
  how_often?: string | null;
  tem_outcome?: string | null;
  limiting_factor?: string | null;
  proposed_grade?: number | null;
  override_flag?: boolean | null;
  override_category?: string | null;
  override_note?: string | null;
  override_actor?: string | null;
  override_at?: string | null;
  development_level?: string | null;
  development_level_source?: string | null;
  /** ICD 9.3 provenance. Read-only here: no edit or restore may move it. */
  historical?: boolean | null;
}

/**
 * The dimensions group (GCH-11, GCH-12). Written all four or not at all:
 * migration 248 ap_dimensions_group_together rejects a partial group, so a
 * half-filled panel is a legitimate editing state that simply does not reach
 * the database yet.
 */
export const DIMENSION_GROUP_FIELDS = [
  'how_many',
  'how_often',
  'tem_outcome',
  'limiting_factor',
] as const;

/**
 * The override group (GCH-13, DEC-28). Travels together for the same reason:
 * ap_override_category_iff_override and ap_override_note_iff_other are per-row
 * invariants, so sending a category without its flag is rejected by the
 * database rather than half-applied.
 *
 * Six fields since G6-04, not four. The last two are ICD 3.1.2's actor and
 * moment, and GAT-12 reads them as part of one record rather than as optional
 * companions to it: "the stored record carries proposed grade, final grade,
 * override flag, category, actor and timestamp". They are listed here so the
 * group is one statement in one place; no draft sets them, because they are
 * stamped by the save rather than typed by the instructor.
 */
export const OVERRIDE_GROUP_FIELDS = [
  'proposed_grade',
  'override_flag',
  'override_category',
  'override_note',
  'override_actor',
  'override_at',
] as const;

/**
 * The per-competency development level and its source (G3-07, ICD 3.1.4).
 * Travels as a pair because the two are one statement: a level without its
 * source says nothing about which rung of the ICD 8.8 authority order set it,
 * and a source without a level says nothing at all. Before G3-14 only a change
 * to the level itself was ever sent, so an instructor who corrected the source
 * alone had that correction accepted on screen and dropped on the way to the
 * database.
 */
export const DEVELOPMENT_LEVEL_GROUP_FIELDS = [
  'development_level',
  'development_level_source',
] as const;

/**
 * What a save knows that the draft cannot (G3-14b, extended by G6-04).
 *
 * `prefill` is the ICD 8.8 code suggested from the session development level,
 * or null when the session offers none. It is a starting value for the Step 6
 * selector, never a stored default: it reaches a row only through a save the
 * instructor asked for, and only on the rows that save carries.
 *
 * `actor` and `at` are the authenticated instructor and the instant the batch
 * was saved. They stamp BOTH audit pairs, which is why there is one pair of
 * fields here and not two: ICD 3.1.4's development-level `assigned_by` and
 * `assigned_at`, and ICD 3.1.2's `override_actor` and `override_at`. The same
 * person pressed save at the same moment, and a save that recorded two
 * different actors or two different instants for one transaction would be
 * describing something that did not happen.
 *
 * Both pairs are client-asserted, under the session the RPC is called with.
 * Migration 249 has always carried all four payload keys; G3-14b wrote the
 * first pair and G6-04 writes the second.
 */
export interface SessionSaveContext {
  prefill?: string | null;
  actor?: string | null;
  at?: string | null;
}

/**
 * True when the Step 6 prefill applies to this row: for the panel that shows
 * it and for the save that writes it.
 *
 * One predicate for both, deliberately. A suggestion the screen shows and a
 * save does not record, or a value a save records that the screen never showed,
 * are the two ways this feature could lie about what an instructor decided.
 *
 * It applies to a row carrying no level of its own, whose selector the
 * instructor has not touched, and only when the session offers a level. Never
 * to a historical row: ICD 9.3 and GCH-35 make an unrecorded value "not
 * recorded", and a record from before the dimensions existed is the plainest
 * case of that rule there is.
 */
export function developmentLevelPrefillApplies(
  baseline: Pick<SessionAssessmentRow, 'development_level' | 'historical'>,
  draft: AssessmentRowDraft | undefined,
  prefill: string | null | undefined,
): boolean {
  if (!prefill) return false;
  if (baseline.historical === true) return false;
  if ((baseline.development_level ?? null) != null) return false;
  return !DEVELOPMENT_LEVEL_GROUP_FIELDS.some((f) => draft?.[f] !== undefined);
}

/** Per-row overrides while editing (only set fields are sent in batch COALESCE updates). */
export interface AssessmentRowDraft {
  score?: number | null;
  analysis?: string | null;
  goals?: string | null;
  evidence?: unknown;
  /** When true, evidence is included in batch even if unchanged vs baseline. */
  evidenceTouched?: boolean;
  // HG-3A G3-02b. See DIMENSION_GROUP_FIELDS / OVERRIDE_GROUP_FIELDS for the
  // group rules that govern how these reach the database.
  how_many?: string | null;
  how_often?: string | null;
  tem_outcome?: string | null;
  limiting_factor?: string | null;
  proposed_grade?: number | null;
  override_flag?: boolean | null;
  override_category?: string | null;
  override_note?: string | null;
  // Typed because the override group is swept as a whole, never because a
  // control writes them: the save stamps this pair, an instructor does not.
  override_actor?: string | null;
  override_at?: string | null;
  development_level?: string | null;
  development_level_source?: string | null;
}

/** Every scalar field a draft may carry, for merge and dirty-detection sweeps. */
const DRAFT_SCALAR_FIELDS = [
  'score',
  'analysis',
  'goals',
  ...DIMENSION_GROUP_FIELDS,
  ...OVERRIDE_GROUP_FIELDS,
  ...DEVELOPMENT_LEVEL_GROUP_FIELDS,
] as const;

export type DraftMap = Record<string, AssessmentRowDraft>;

export function createEmptyDraftMap(): DraftMap {
  return {};
}

export function createEmptyDirtySet(): Set<string> {
  return new Set();
}

/** Filter pilot rows for the active crew role (null pilot_role rows stay visible). */
export function filterAssessmentsForPilotRole<T extends { pilot_role?: string | null }>(
  rows: T[],
  role: string | null,
): T[] {
  if (!role) return rows;
  return rows.filter((a) => a.pilot_role === role || a.pilot_role == null);
}

/**
 * The order Step 6 lists competencies in (G6-05, GAT-02).
 *
 * Sibling of the filter above, and for the same reason: that one decides WHICH
 * rows the selector offers, this one decides in what order, and both are
 * decisions about the same control. Every Step 6 surface reads its rows from
 * one loaded array, so ordering that array once puts the phone pills, the
 * desktop pill bar, the sidebar and the competency the page opens on into the
 * same order without four places agreeing to do so.
 *
 * The defect this closes: the page asked PostgREST for
 * `.order('competency', { ascending: true })` and listed COM, FPA, FPM, KNO on
 * the very screen an instructor grades through, while the report view beside
 * it read KNO, FPM, PRO from the shared order. GAT-02 says "every competency
 * list in the product", and a selector is a competency list in the product.
 *
 * The rule itself is not restated here. sortByFrameworkListingOrder is where
 * it lives, OPR-last default and all, so the selector and the report cannot
 * drift apart again by one of them being edited.
 */
export function orderAssessmentsForSelector<T extends { competency: string }>(
  rows: readonly T[],
  listingOrder?: readonly string[],
): T[] {
  return sortByFrameworkListingOrder(rows, (row) => row.competency, listingOrder);
}

/** Merge a partial patch into the draft for a row and return the updated map. */
export function applyDraftPatch(
  drafts: DraftMap,
  assessmentId: string,
  patch: AssessmentRowDraft,
): DraftMap {
  return {
    ...drafts,
    [assessmentId]: {
      ...drafts[assessmentId],
      ...patch,
    },
  };
}

/** Full-row flush (e.g. before switching competency) — marks all editable fields. */
export function draftFromRow<T extends SessionAssessmentRow>(row: T): AssessmentRowDraft {
  return {
    score: row.score,
    analysis: row.analysis,
    goals: row.goals,
    evidence: row.evidence,
    evidenceTouched: false,
    // Carried so switching competency mid-capture does not silently discard
    // dimensions the instructor has already chosen.
    how_many: row.how_many ?? null,
    how_often: row.how_often ?? null,
    tem_outcome: row.tem_outcome ?? null,
    limiting_factor: row.limiting_factor ?? null,
    proposed_grade: row.proposed_grade ?? null,
    override_flag: row.override_flag ?? null,
    override_category: row.override_category ?? null,
    override_note: row.override_note ?? null,
    development_level: row.development_level ?? null,
    development_level_source: row.development_level_source ?? null,
  };
}

/**
 * Effective row for UI binding: baseline merged with draft overrides.
 */
export function getEffectiveRow<T extends SessionAssessmentRow>(
  baseline: T,
  drafts: DraftMap,
): T {
  const draft = drafts[baseline.id];
  if (!draft) return baseline;

  const merged: Record<string, unknown> = { ...(baseline as Record<string, unknown>) };
  for (const field of DRAFT_SCALAR_FIELDS) {
    const value = draft[field];
    if (value !== undefined) merged[field] = value;
  }
  if (draft.evidence !== undefined) merged.evidence = draft.evidence;
  // `historical` is never merged from a draft: it is record provenance, not
  // content, and no edit may move it (ICD 9.3, migration 248).
  return merged as T;
}

/** True once every member of the dimensions group carries a value. */
export function dimensionGroupComplete(row: Partial<SessionAssessmentRow>): boolean {
  return DIMENSION_GROUP_FIELDS.every((f) => {
    const v = row[f];
    return v !== null && v !== undefined && v !== '';
  });
}

export function isDirtySet(dirtyIds: Set<string>): boolean {
  return dirtyIds.size > 0;
}

export function getDirtyCount(dirtyIds: Set<string>): number {
  return dirtyIds.size;
}

/**
 * Build RPC batch payload for dirty rows only.
 * NULL fields → COALESCE in create_assessment_version (keep existing DB value).
 */
export function buildBatchUpdates<T extends SessionAssessmentRow>(
  baselineRows: T[],
  drafts: DraftMap,
  dirtyIds: Set<string>,
  save: SessionSaveContext = {},
): BatchAssessmentUpdate[] {
  const byId = new Map(baselineRows.map((r) => [r.id, r]));

  return [...dirtyIds]
    .map((id) => {
      const baseline = byId.get(id);
      const draft = drafts[id];
      if (!baseline || !draft) return null;

      const update: BatchAssessmentUpdate = { assessmentId: id };

      if (draft.score !== undefined) {
        update.score = draft.score;
      }
      if (draft.analysis !== undefined) {
        update.analysis = draft.analysis;
      }
      if (draft.goals !== undefined) {
        update.goals = draft.goals;
      }
      // Only send evidence when explicitly edited, never on
      // score/analysis/goals-only saves.
      if (draft.evidenceTouched) {
        update.evidence =
          draft.evidence !== undefined ? draft.evidence : baseline.evidence;
      }

      // HG-3A G3-02b. Both groups are read from the EFFECTIVE row, not from the
      // draft, so a save that touches one member sends the other three at their
      // current values rather than leaving the database to reconcile a partial
      // write it would reject anyway.
      const effective = getEffectiveRow(baseline, drafts);

      const dimensionsTouched = DIMENSION_GROUP_FIELDS.some((f) => draft[f] !== undefined);
      if (dimensionsTouched && dimensionGroupComplete(effective)) {
        for (const f of DIMENSION_GROUP_FIELDS) {
          update[f] = effective[f] as string;
        }
      }
      // Deliberately silent when the group is incomplete: migration 248 would
      // reject a partial group, and a half-filled panel is a normal editing
      // state, not an error to report.

      const overrideTouched = OVERRIDE_GROUP_FIELDS.some((f) => draft[f] !== undefined);
      if (overrideTouched) {
        // G6-04: the shared builder constructs the record, rather than this
        // file assembling one that has to agree with it. buildOverrideRecord
        // is where DEC-28 is written down (a category only on an override, a
        // note only for `other`, the flag stated rather than left null), and
        // until now it had no caller outside its own tests while the save path
        // emitted a hand-built four-field copy of the same idea. One
        // construction point is the whole of the fix: the record cannot be
        // internally inconsistent, because nothing else builds one.
        const record = buildOverrideRecord({
          recordedGrade: (effective.score ?? null) as number | null,
          proposedGrade: (effective.proposed_grade ?? null) as number | null,
          category: (effective.override_category ?? null) as OverrideCategory | null,
          note: (effective.override_note ?? null) as string | null,
          actor: save.actor ?? null,
          at: save.at ?? null,
        });

        update.proposed_grade = record.proposed_grade;
        update.override_flag = record.override_flag;
        update.override_category = record.override_category;
        update.override_note = record.override_note;

        // ICD 3.1.2's actor and moment, matching the G3-14b precedent below in
        // form as well as in substance: omitted rather than sent null, because
        // the RPC COALESCEs these two columns and a null would only mean
        // "leave the stored value alone". A save that records no override
        // therefore sends neither key, which is why a row that never had an
        // override never acquires an actor.
        if (record.override_actor != null) {
          update.override_actor = record.override_actor;
        }
        if (record.override_at != null) {
          update.override_at = record.override_at;
        }
      }

      // Read from the effective row for the same reason the two groups above
      // are: a save that touches one member sends the other at its current
      // value, so the pair can never reach the database half-stated. Touching
      // EITHER member sends both, which is the G3-14 correction: the source
      // selector is an editable control in its own right, and gating on the
      // level alone made a source-only edit unsaveable and silent.
      const developmentLevelTouched = DEVELOPMENT_LEVEL_GROUP_FIELDS.some(
        (f) => draft[f] !== undefined,
      );
      // G3-14b, decision C: a row the instructor SAVES with no level of its own
      // records the session's suggestion as the instructor's own act. The save
      // is the confirming act, which is why nothing is written for a row that
      // is not in this batch and nothing is written anywhere but here.
      const prefillApplies = developmentLevelPrefillApplies(
        baseline,
        draft,
        save.prefill,
      );

      if (developmentLevelTouched || prefillApplies) {
        const level = prefillApplies
          ? (save.prefill as string)
          : ((effective.development_level ?? null) as string | null);
        // ICD 8.8 rung 2. A confirmed prefill is an instructor decision, which
        // is what instructor_override says and what default_mapping, the
        // automated-recommendation rung, would misdescribe (G3-14 section 2.4).
        // A source without a level says nothing, so a cleared level clears it.
        const source =
          level == null
            ? null
            : prefillApplies
              ? 'instructor_override'
              : ((effective.development_level_source ?? 'instructor_override') as string);

        update.development_level = level;
        update.development_level_source = source;

        // ICD 3.1.4: assigned_by is the actor "where source is
        // instructor_override", assigned_at when the level took effect. Both
        // payload keys have existed since migration 249; this is their first
        // writer. Omitted rather than sent null, because the RPC COALESCEs
        // these two and a null would only mean "leave the stored value alone".
        if (level != null && source === 'instructor_override') {
          if (save.actor != null) {
            update.development_level_assigned_by = save.actor;
          }
          if (save.at != null) {
            update.development_level_assigned_at = save.at;
          }
        }
      }

      return update;
    })
    .filter((u): u is BatchAssessmentUpdate => u !== null);
}

/**
 * The row as it must look in memory once a batch save has succeeded.
 *
 * This exists because the save write-back had the SAME defect the restore
 * write-back had, and only the restore half was ever corrected (see
 * assessment-restore.ts, whose header describes the identical failure mode).
 * The editor rebuilt the saved row by spreading the STALE baseline and copying
 * back only score, analysis, goals and evidence, so every field that lives
 * only on the draft was reinstated at its pre-edit value. On a freshly
 * AI-generated row that meant the whole HG-3A capture group reverted to null
 * the instant it was saved: the database was right and the screen was wrong,
 * and the export built from that screen printed "Not recorded" over dimensions
 * the instructor had just recorded, which inverts GCH-35.
 *
 * The merge is delegated to getEffectiveRow rather than restated as a field
 * list, deliberately. A hand-maintained list is what drifted in the first
 * place; DRAFT_SCALAR_FIELDS is the single source, so a field added to the
 * draft is carried here automatically and can never be forgotten again.
 *
 * The saved batch item is applied on top of that merge (G3-14b) because the
 * development-level pair can now be written by a save without ever passing
 * through the draft: a confirmed prefill is a value the instructor accepted
 * rather than typed. Adopting exactly what was SENT is what keeps the screen,
 * an export built from it and the stored row saying the same thing, which is
 * the failure this function exists to prevent.
 *
 * The override audit pair is adopted here too, since G6-04 gave it a writer.
 *
 * WHAT REMAINS TRUE AND IS NOT PAPERED OVER: an override that is REMOVED does
 * not clear the pair. The RPC COALESCEs override_actor and override_at, so a
 * client cannot send a null that lands, and the save omits the keys rather
 * than sending nulls that would do nothing. The row therefore keeps the actor
 * and moment of the override it used to carry, beside override_flag false.
 * Nothing renders it: capturePresentation reads the pair only when the flag is
 * true. It is stated here because the alternative to stating it is a comment
 * that says the pair is written, full stop, which would be the half of the
 * truth that flatters the code. Clearing it needs the same key-presence
 * treatment migration 268 gave the development-level pair.
 */
export function applySavedBatchToRow<T extends SessionAssessmentRow>(
  baseline: T,
  drafts: DraftMap,
  versionNumber: number,
  savedUpdate?: BatchAssessmentUpdate,
): T {
  const merged = { ...getEffectiveRow(baseline, drafts), version: versionNumber };

  if (savedUpdate) {
    // The row is a generic here, so the overlay is applied through a plain
    // record view. The keys are the six named below and nothing else.
    const writable = merged as unknown as Record<string, unknown>;
    for (const key of SAVED_STAMPED_KEYS) {
      const value = savedUpdate[key];
      if (value !== undefined) writable[key] = value;
    }
  }

  return merged as T;
}

/**
 * The keys a save may write that no draft carries. Listed rather than swept
 * from the batch item so an unrelated field can never be adopted here by
 * accident, the way the hand-maintained field list above once drifted.
 *
 * The two override keys join the four development-level ones under G6-04, for
 * the reason the four are here at all: a value the save wrote and the screen
 * did not adopt is a divergence between the row and the picture of it, and
 * this function exists to prevent exactly that.
 */
const SAVED_STAMPED_KEYS = [
  'development_level',
  'development_level_source',
  'development_level_assigned_by',
  'development_level_assigned_at',
  'override_actor',
  'override_at',
] as const;

/**
 * Apply saved rows from DB after batch save — returns cleared draft state.
 */
export function clearDraftState(): { drafts: DraftMap; dirtyIds: Set<string> } {
  return { drafts: createEmptyDraftMap(), dirtyIds: createEmptyDirtySet() };
}

/**
 * Detect whether a draft differs from baseline (for optional UI hints).
 */
/** Save button label for session batch editors (Phase 4.1). */
export function getSessionSaveButtonLabel(isSaving: boolean, dirtyCount: number): string {
  if (isSaving) return 'Saving...';
  if (dirtyCount > 1) return `Save all changes (${dirtyCount})`;
  return 'Save';
}

export function rowDiffersFromBaseline<T extends SessionAssessmentRow>(
  baseline: T,
  draft: AssessmentRowDraft | undefined,
): boolean {
  if (!draft) return false;

  for (const field of DRAFT_SCALAR_FIELDS) {
    const value = draft[field];
    if (value !== undefined && value !== (baseline as Record<string, unknown>)[field]) return true;
  }
  if (draft.evidenceTouched || draft.evidence !== undefined) {
    if (draft.evidence !== undefined && draft.evidence !== baseline.evidence) return true;
    if (draft.evidenceTouched) return true;
  }
  return false;
}
